import { useEffect, useState } from "react";
import { getSettings, saveSettings } from "@/lib/api";
import { Key, Storefront, ArrowSquareOut, Check, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Settings() {
  const [s, setS] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getSettings().then(setS); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saveSettings(s);
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="p-8 text-arena-muted">Loading…</div>;

  return (
    <div className="p-8 max-w-4xl" data-testid="settings-page">
      <div className="mb-6">
        <div className="overline mb-2">// configuration</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">SETTINGS</h1>
      </div>

      {/* Store */}
      <div className="border border-arena-border bg-arena-surface p-6 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <Storefront size={18} weight="bold" className="text-arena-primary" />
          <div className="font-heading text-lg font-semibold">STORE INFO</div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="overline block mb-1">Store Name</label>
            <input
              data-testid="input-store-name"
              value={s.store_name}
              onChange={e => setS({ ...s, store_name: e.target.value })}
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
            />
          </div>
          <div>
            <label className="overline block mb-1">Store URL</label>
            <input
              data-testid="input-store-url"
              value={s.store_url}
              onChange={e => setS({ ...s, store_url: e.target.value })}
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
            />
          </div>
        </div>
      </div>

      {/* Sellvia Keys */}
      <div className="border border-arena-border bg-arena-surface p-6 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Key size={18} weight="bold" className="text-arena-primary" />
          <div className="font-heading text-lg font-semibold">SELLVIA CREDENTIALS</div>
        </div>
        <p className="text-xs text-arena-muted mb-4">
          These are the Public + Secret keys from your Sellvia dashboard (My Account → API Keys, or your welcome email).
          Stored server-side. Note: Sellvia does not expose a public REST API — keys are held for future integrations & reference.
        </p>
        <div className="space-y-3">
          <div>
            <label className="overline block mb-1">Public Key</label>
            <input
              data-testid="input-public-key"
              value={s.sellvia_public_key}
              onChange={e => setS({ ...s, sellvia_public_key: e.target.value })}
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none font-mono"
            />
          </div>
          <div>
            <label className="overline block mb-1">Secret Key</label>
            <div className="relative">
              <input
                data-testid="input-secret-key"
                type={showSecret ? "text" : "password"}
                value={s.sellvia_secret_key}
                onChange={e => setS({ ...s, sellvia_secret_key: e.target.value })}
                className="w-full bg-arena-bg border border-arena-border px-3 py-2 pr-10 text-sm focus:border-arena-primary outline-none font-mono"
              />
              <button
                onClick={() => setShowSecret(v => !v)}
                data-testid="toggle-secret"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-arena-muted hover:text-arena-primary"
              >
                {showSecret ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
              </button>
            </div>
          </div>
          <div>
            <label className="overline block mb-1">Sellvia Dashboard URL</label>
            <input
              data-testid="input-dashboard-url"
              value={s.sellvia_dashboard_url}
              onChange={e => setS({ ...s, sellvia_dashboard_url: e.target.value })}
              placeholder="https://app.sellvia.com/"
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none font-mono"
            />
            <p className="text-[10px] text-arena-muted mt-1">Where the &ldquo;Process on Sellvia&rdquo; button opens. Change if your dashboard is at a different URL.</p>
          </div>
        </div>
      </div>

      {/* Stripe */}
      <div className="border border-arena-border bg-arena-surface p-6 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Key size={18} weight="bold" className="text-arena-primary" />
          <div className="font-heading text-lg font-semibold">STRIPE (DIRECT-TO-BANK)</div>
          <span className={`ml-2 text-[9px] tracking-widest uppercase px-2 py-0.5 ${s.stripe_mode === 'live' ? 'bg-arena-success text-black' : 'bg-arena-warning text-black'}`}>
            {s.stripe_mode || 'test'} mode
          </span>
        </div>
        <p className="text-xs text-arena-muted mb-4">
          Money from checkouts goes <strong className="text-arena-primary">100% to your Stripe account</strong> → your bank. No Sellvia payment fees.
          Leave blank to use our shared <em>test key</em> (safe for development, no real charges). Paste your own <code className="text-arena-primary">sk_live_...</code> key to accept real money.
        </p>
        <div className="space-y-3">
          <div>
            <label className="overline block mb-1">Your Stripe Secret Key</label>
            <input
              data-testid="input-stripe-key"
              type={showSecret ? "text" : "password"}
              value={s.stripe_secret_key}
              onChange={e => setS({ ...s, stripe_secret_key: e.target.value })}
              placeholder="sk_live_... (leave empty to use test mode)"
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none font-mono"
            />
            <p className="text-[10px] text-arena-muted mt-1">
              Get yours at <a className="text-arena-primary hover:underline" href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">dashboard.stripe.com/apikeys</a>. Never share this key.
            </p>
          </div>
          <div>
            <label className="overline block mb-1">Mode</label>
            <select
              data-testid="input-stripe-mode"
              value={s.stripe_mode || 'test'}
              onChange={e => setS({ ...s, stripe_mode: e.target.value })}
              className="w-full bg-arena-bg border border-arena-border px-3 py-2 text-sm focus:border-arena-primary outline-none"
            >
              <option value="test">Test (safe — no real charges)</option>
              <option value="live">Live (real money)</option>
            </select>
          </div>
        </div>
      </div>

      {/* How-to card */}
      <div className="border border-arena-border bg-arena-surface p-6 mb-4">
        <div className="font-heading text-lg font-semibold mb-3">// HOW THE BOT PROCESSES ORDERS</div>
        <ol className="text-xs text-arena-text space-y-2 leading-relaxed">
          <li><span className="text-arena-primary font-bold">1.</span> A customer buys from <span className="text-arena-primary">pepstyles.store</span> via a Stripe payment link you shared</li>
          <li><span className="text-arena-primary font-bold">2.</span> The order auto-appears in your <span className="text-arena-primary">Orders</span> queue with customer info + shipping address</li>
          <li><span className="text-arena-primary font-bold">3.</span> Click <span className="text-arena-primary font-bold">PROCESS</span> &mdash; PepStyles HQ copies order details to clipboard + opens Sellvia dashboard</li>
          <li><span className="text-arena-primary font-bold">4.</span> Paste + confirm payment in Sellvia. They ship, track, deliver</li>
          <li><span className="text-arena-primary font-bold">5.</span> Update status in PepStyles HQ → shipped → delivered. PepBot handles customer questions in between.</li>
          <li><span className="text-arena-primary font-bold">4.</span> Paste + confirm payment in Sellvia. They ship, track, deliver</li>
          <li><span className="text-arena-primary font-bold">5.</span> Update status in Viral.Arena → shipped → delivered. Bot handles customer questions in between.</li>
        </ol>
        <a
          href="https://sellvia.com/blog/order-processing-my-first-order/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sellvia-docs-link"
          className="inline-flex items-center gap-1 mt-4 text-xs text-arena-primary hover:underline tracking-widest uppercase"
        >
          Sellvia docs <ArrowSquareOut size={12} weight="bold" />
        </a>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="save-settings-btn"
        className="bg-arena-primary text-black px-6 py-3 text-xs tracking-widest uppercase font-bold hover:bg-arena-primaryHover disabled:opacity-50 flex items-center gap-2"
      >
        <Check size={14} weight="bold" />
        {saving ? "Saving..." : "Save All Settings"}
      </button>
    </div>
  );
}
